/**
 * Parse API 422 validation errors into a map of field key -> message
 * for display next to form fields.
 *
 * Backend returns: { error, detail, details: [ { field: "body.naam", message, code }, ... ], status_code, timestamp }
 * We strip "body." prefix so "body.naam" -> "naam", "body.line_items.0.quantity" -> "line_items.0.quantity"
 *
 * i18n: Fallback messages use i18n.t() for translation (non-React module).
 */

import i18n from '../i18n/config'

export interface ApiErrorDetail {
  field?: string
  message: string
  code?: string
}

export interface ApiErrorResponse {
  error?: string
  detail?: string
  details?: ApiErrorDetail[]
  status_code?: number
}

/**
 * Get field-level errors from an API error response (422).
 * Returns a record: { "naam": "Naam is verplicht", "materiaal_groep_id": "..." }
 * For nested fields we keep the path, e.g. "line_items.0.description"
 */
export function get422FieldErrors(responseData: unknown): Record<string, string> {
  const out: Record<string, string> = {}
  const data = responseData as ApiErrorResponse
  const details = data?.details
  if (!Array.isArray(details)) return out

  for (const d of details) {
    const msg = d.message || i18n.t('validation.generic', 'Validatiefout')
    let key = d.field ?? ''
    // Strip "body." prefix so we can match form field names
    if (key.startsWith('body.')) key = key.slice(5)
    if (key) {
      // Keep first occurrence per key (or concatenate; we keep first for simplicity)
      if (!out[key]) out[key] = msg
    }
  }
  return out
}

/**
 * Get a single generic error message from any API error (for toast or banner).
 * Ondersteunt ook blob responses (bijv. wanneer server per ongeluk file retourneert i.p.v. JSON).
 */
export async function getApiErrorMessageAsync(error: unknown): Promise<string> {
  if (error && typeof error === 'object' && 'response' in error) {
    const ax = error as { response?: { data?: unknown; status?: number } }
    const data = ax.response?.data
    if (data instanceof Blob) {
      try {
        const text = await data.text()
        const parsed = JSON.parse(text) as ApiErrorResponse
        if (typeof parsed?.detail === 'string') return parsed.detail
        if (parsed?.details?.length) return parsed.details[0]?.message ?? text
        return text || i18n.t('errors.generic', 'Er is een fout opgetreden')
      } catch {
        return i18n.t('errors.generic', 'Er is een fout opgetreden')
      }
    }
    const apiData = data as ApiErrorResponse | undefined
    const detail = apiData?.detail
    if (typeof detail === 'string') return detail
    if (apiData?.details?.length) {
      const first = apiData.details[0]?.message
      if (first) return first
    }
  }
  if (error instanceof Error) return error.message
  return i18n.t('errors.generic', 'Er is een fout opgetreden')
}

/**
 * Get a single generic error message from any API error (for toast or banner).
 * Synchrone variant - bij blob response wordt generieke melding getoond.
 */
export function getApiErrorMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'response' in error) {
    const ax = error as { response?: { data?: unknown; status?: number } }
    const data = ax.response?.data
    if (data instanceof Blob) {
      return i18n.t('errors.generic', 'Er is een fout opgetreden')
    }
    const apiData = data as ApiErrorResponse | undefined
    const detail = apiData?.detail
    if (typeof detail === 'string') return detail
    if (apiData?.details?.length) {
      const first = apiData.details[0]?.message
      if (first) return first
    }
  }
  if (error instanceof Error) return error.message
  return i18n.t('errors.generic', 'Er is een fout opgetreden')
}
